import { useEffect, useRef, useState } from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router";
import { NoteIcon } from "../../../../../assets";
import { ApiError } from "../../../../models";
import {
  useGetSpecificNotesQuery,
  useLegislationArticleByIdQuery,
} from "../../../../services";
import {
  handleInternalSpaNavigation,
  processDocumentText,
} from "../../../../utils";
import { PDLTypography } from "../../dna";
import LexLoading from "../_lexLoading";
import { Toggle } from "../_toggle";
import "./index.scss";

export type NotesModalType = "note" | "legislation";

interface NotesModalProps {
  showNoteModal: boolean;
  setShowNoteModal: (b: boolean) => void;
  noteTitle: string;
  type: NotesModalType;
  noteContent: string;
  articleId?: string;
  url?: string;
}

const NotesModal = ({
  setShowNoteModal,
  showNoteModal,
  noteTitle,
  noteContent,
  type,
  articleId,
  url,
}: NotesModalProps) => {
  const [hideRevoked, setHideRevoked] = useState(false);

  const [internalType, setInternalType] = useState<NotesModalType>(type);
  const [internalTitle, setInternalTitle] = useState(noteTitle);
  const [internalContent, setInternalContent] = useState(noteContent);
  const [internalArticleId, setInternalArticleId] = useState(articleId || "");
  const [internalUrl, setInternalUrl] = useState(url || "");
  const [notas, setNotas] = useState<string[]>([]);

  const navigate = useNavigate();

  const contentRef = useRef<HTMLDivElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);

  const { data, isPending, isError, error } = useLegislationArticleByIdQuery(
    internalArticleId,
    internalType === "legislation" && showNoteModal && !!internalArticleId,
  );

  const queryError = error ? (error as ApiError) : null;
  const errorMsg =
    queryError?.response?.status === 404
      ? "Estamos trabalhando para disponibilizá-lo em breve."
      : (queryError as ApiError)?.response?.data?.message ||
        "Não foi possível encontrar o conteúdo do artigo";

  const legislationId = internalArticleId.replace(/art\d+$/i, "");

  const { data: fetchedNotes, isPending: notesPending } =
    useGetSpecificNotesQuery(
      legislationId,
      notas,
      internalType === "note" && notas.length > 0,
    );

  useEffect(() => {
    const el = contentRef.current;

    if (!el) return;

    const onClick = (event: any) => {
      const target = event.target;

      if (target.classList?.contains("nota-super")) {
        let next = target.nextElementSibling;

        while (next) {
          if (next.className?.includes("nota-texto")) {
            next.classList.toggle("oculto");
            break;
          }
          next = next.nextElementSibling;
        }
        return;
      }

      if (target.hasAttribute?.("data-nota-link")) {
        event.preventDefault();
        const noteKey = target.getAttribute("data-nota-link");
        if (!noteKey) return;

        const legislationId =
          data?.documento?.id || internalArticleId?.replace(/art\d+$/i, "");

        if (!legislationId) return;

        setInternalType("note");
        setInternalTitle(`Nota ${noteKey.slice(4, 99999)}`);

        setNotas([noteKey.replace("nota", "")]);

        return;
      }

      const link = event.target.closest("a");

      if (link?.hasAttribute("data-full-link")) {
        return;
      }

      if (link) {
        const href = link.getAttribute("href") || "";

        const url = new URL(href, window.location.origin);
        const artIdFromQuery = url.searchParams.get("artId");
        const articleMatch = href.match(/([A-Za-z]+art\d+)/i);

        const articleId = artIdFromQuery || (articleMatch && articleMatch[0]);

        if (articleId) {
          event.preventDefault();

          setInternalType("legislation");
          setInternalArticleId(articleId);
          setInternalUrl(href);

          return;
        } else {
          if (handleInternalSpaNavigation(href, event, navigate)) {
            return;
          }
        }
      }
    };

    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, [data, internalContent, internalArticleId, showNoteModal]);

  useEffect(() => {
    if (!fetchedNotes?.notas?.length) return;

    const nota = fetchedNotes.notas[0];
    if (nota) {
      setInternalContent(
        processDocumentText({
          htmlText: nota.text || nota.texto || "Nota não possui texto",
          hideRevoked,
        }),
      );
    }
  }, [fetchedNotes, hideRevoked]);

  useEffect(() => {
    if (showNoteModal) {
      setInternalType(type);
      setInternalTitle(noteTitle);
      setInternalContent(noteContent);
      setInternalArticleId(articleId || "");
    }
  }, [showNoteModal, type, noteTitle, noteContent, articleId]);

  useEffect(() => {
    if (!showNoteModal) {
      setInternalType(type);
      setInternalTitle(noteTitle);
      setInternalContent(noteContent);
      setInternalArticleId(articleId || "");
      setNotas([]);
      setHideRevoked(false);
    }
  }, [showNoteModal, type, noteTitle, noteContent, articleId]);

  useEffect(() => {
    if (
      !internalUrl ||
      !showNoteModal ||
      !data?.artigo ||
      isPending ||
      !contentRef.current ||
      !modalBodyRef.current
    ) {
      return;
    }

    const hashMatch = internalUrl.match(/#([^#]+)$/);
    const hash = hashMatch?.[1];
    if (!hash) return;

    const container = modalBodyRef.current;

    const scrollToParagraph = () => {
      const element = contentRef.current!.querySelector<HTMLElement>(
        `#${CSS.escape(hash)}`,
      );
      if (!element) return;

      element.classList.add("highlight-scroll");
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const offset =
        elementRect.top -
        containerRect.top +
        container.scrollTop -
        container.clientHeight / 2;

      container.scrollTo({
        top: offset,
        behavior: "smooth",
      });
    };

    const timers = [
      setTimeout(scrollToParagraph, 100),
      setTimeout(scrollToParagraph, 300),
      setTimeout(scrollToParagraph, 600),
    ];

    return () => timers.forEach(clearTimeout);
  }, [internalUrl, data, isPending, showNoteModal]);

  useEffect(() => {
    if (url) setInternalUrl(url);
  }, [url]);

  return (
    <Modal
      backdrop="static"
      contentClassName="h-100 d-flex flex-column align-items-stretch justify-content-between gap-4"
      dialogClassName="h-100"
      className="d-flex flex-row align-items-stretch justify-content-end"
      show={showNoteModal}
      onHide={() => setShowNoteModal(false)}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {internalType === "note"
            ? internalTitle
            : data?.documento?.titulo || data?.documento?.nome}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ overflowY: "auto" }} ref={modalBodyRef}>
        <div ref={contentRef} id="modal">
          {internalType === "legislation" ? (
            isPending ? (
              <Container>
                <Row>
                  <Col>
                    <div className="content-loading">
                      <LexLoading size={100} customText="Buscando artigo..." />
                    </div>
                  </Col>
                </Row>
              </Container>
            ) : isError ? (
              <Container>
                <Row>
                  <Col>
                    <div className="text-center mt-5">
                      <PDLTypography
                        size="xl"
                        variant="primary"
                        weight="medium"
                      >
                        Este documento ainda não está disponível.
                      </PDLTypography>
                      <p className="text-muted">{errorMsg}</p>
                      <p className="text-muted">
                        Se for urgente, fale com a gente. Teremos prazer em
                        ajudar.
                      </p>
                    </div>
                  </Col>
                </Row>
              </Container>
            ) : (
              <>
                <div className="modal-show-full-note-container">
                  <Link
                    className="modal-show-full-note"
                    data-full-link="true"
                    to={`/legislacao/${data?.documento?.id}#${data?.artigo?.ref}`}
                  >
                    <NoteIcon /> Ver norma completa
                  </Link>
                </div>

                <Toggle
                  label="Texto compilado"
                  checked={hideRevoked}
                  onChange={() => setHideRevoked(!hideRevoked)}
                />

                <div
                  className="note-content"
                  dangerouslySetInnerHTML={{
                    __html: processDocumentText({
                      htmlText: data?.artigo.texto || "",
                      hideRevoked,
                    }),
                  }}
                />
              </>
            )
          ) : notesPending && notas.length > 0 ? (
            <Container>
              <Row>
                <Col>
                  <div className="content-loading">
                    <LexLoading size={100} customText="Buscando conteúdo..." />
                  </div>
                </Col>
              </Row>
            </Container>
          ) : (
            <div
              className="note-content"
              dangerouslySetInnerHTML={{ __html: internalContent }}
            />
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default NotesModal;
